import React from 'react';
import '../styles/tavern-components.css';

export interface TavernCardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'event';
  onClick?: () => void;
}

/**
 * Parchment-styled card component
 * 
 * @example
 * <TavernCard variant="event">
 *   <TavernCardHeader>
 *     <TavernCardTitle>Dragon Slaying Quest</TavernCardTitle>
 *     <TavernCardMeta>
 *       <span>14:00 - 17:00</span>
 *       <OpenBadge />
 *     </TavernCardMeta>
 *   </TavernCardHeader>
 *   <div>Card content...</div>
 * </TavernCard>
 */
export function TavernCard({
  children,
  className = '',
  variant = 'default',
  onClick,
}: TavernCardProps) {
  const variantClass = variant === 'event' ? 'tavern-card--event' : 'ttbCard';
  const clickableClass = onClick ? 'tavern-card--clickable' : '';

  return (
    <div
      className={`${variantClass} ${clickableClass} ${className}`.trim()}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </div>
  );
}

export interface TavernCardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function TavernCardHeader({
  children,
  className = '',
}: TavernCardHeaderProps) {
  return <div className={`tavern-card-header ${className}`.trim()}>{children}</div>;
}

export interface TavernCardTitleProps {
  children: React.ReactNode;
  className?: string;
}

export function TavernCardTitle({
  children,
  className = '',
}: TavernCardTitleProps) {
  return <h3 className={`tavern-card-title ${className}`.trim()}>{children}</h3>;
}

export interface TavernCardMetaProps {
  children: React.ReactNode;
  className?: string;
}

export function TavernCardMeta({
  children,
  className = '',
}: TavernCardMetaProps) {
  return <div className={`tavern-card-meta ${className}`.trim()}>{children}</div>;
}

export interface TavernCardTimeProps {
  timeRange: string;
  dateLabel?: string;
  className?: string;
}

export function TavernCardTime({
  timeRange,
  dateLabel,
  className = '',
}: TavernCardTimeProps) {
  return (
    <span className={`tavern-card-time ${className}`.trim()}>
      {timeRange}
      {dateLabel ? ` · ${dateLabel}` : ''}
    </span>
  );
}
